/**
 * HR API Service
 * Handles certificate verification and search operations
 */

import { supabase } from './supabaseClient';
import { createHashNotFoundAlert } from './alertService';
import { extractCertificateWithGemini } from './geminiOCRService';

/**
 * Verify a certificate by file, CID, or hash
 * @param {Object} params - { file?, cid?, hash?, verifierEmail?, verifierName? }
 * @returns {Promise<Object>} Verification result
 */
export async function verifyCertificate(params) {
  try {
    const { file, cid, hash, verifierEmail, verifierName } = params;
    let fileHash = hash;
    let certificate = null;
    let ocrData = null;

    // If file is provided, calculate hash and perform OCR
    if (file) {
      fileHash = await calculateFileHash(file);
      
      // Perform OCR to extract certificate data
      try {
        console.log('🔍 Performing OCR on uploaded certificate...');
        ocrData = await extractCertificateWithGemini(file);
        console.log('✅ OCR extraction successful:', ocrData);
      } catch (ocrError) {
        console.warn('⚠️ OCR extraction failed:', ocrError);
        // Continue with verification even if OCR fails
      }
    }

    // Try real database first
    if (supabase) {
      try {
        // Search by hash first (most specific)
        if (fileHash) {
          const { data, error } = await supabase
            .from('certificates')
            .select('*')
            .ilike('file_hash', fileHash)
            .single();

          if (!error && data) {
            certificate = data;
          }
        }

        // Search by CID if no match found
        if (!certificate && cid) {
          const { data, error } = await supabase
            .from('certificates')
            .select('*')
            .ilike('cid', cid)
            .single();

          if (!error && data) {
            certificate = data;
          }
        }
      } catch (dbError) {
        console.warn('Database verification failed:', dbError);
      }
    }

    // Determine status
    let status = 'NOT FOUND';
    if (certificate) {
      // Check authentication result if available
      if (certificate.authentication_result) {
        const authResult = certificate.authentication_result;
        if (authResult.isAuthentic === false) {
          status = 'FAKE';
        } else if (authResult.confidence && authResult.confidence < 0.7) {
          status = 'SUSPICIOUS';
        } else {
          status = 'VALID';
        }
      } else {
        status = 'VALID'; // Default to valid if found in DB
      }
    } else {
      // Certificate not found - create alert for the institution
      console.log('🔔 Certificate not found, creating alert...');
      
      // Use OCR extracted data if available, otherwise try file name
      let studentName = ocrData?.studentName || null;
      let rollNumber = ocrData?.rollNumber || null;
      let institutionName = ocrData?.institution || null;
      
      if (!studentName) {
        const fileName = file?.name || 'Unknown';
        const studentNameMatch = fileName.match(/([A-Z][a-z]+ [A-Z][a-z]+)/);
        studentName = studentNameMatch ? studentNameMatch[1] : null;
      }
      
      // Create alert (will be sent to all institutions or specific institution if we can determine it)
      await createHashNotFoundAlert({
        studentName: studentName || 'Unknown Student',
        rollNumber: rollNumber,
        certificateHash: fileHash,
        certificateCid: cid,
        institutionId: null, // Will notify all institutions
        institutionName: institutionName,
        verificationAttemptBy: verifierEmail || verifierName || 'HR Verifier',
        additionalDetails: {
          fileName: file?.name,
          fileSize: file?.size,
          verificationMethod: file ? 'file_upload' : cid ? 'cid_search' : 'hash_search',
          ocrExtracted: ocrData ? true : false
        }
      }).catch(err => console.error('Failed to create alert:', err));
    }

    // Return verification result
    return {
      status,
      // Prefer database data, fallback to OCR data if available
      studentName: certificate?.student_name || certificate?.studentName || ocrData?.studentName || null,
      institution: certificate?.institution || ocrData?.institution || null,
      rollNumber: certificate?.roll_number || certificate?.rollNumber || ocrData?.rollNumber || null,
      certificateType: certificate?.certificate_type || certificate?.certificateType || ocrData?.certificateType || null,
      fileHash: certificate?.file_hash || certificate?.fileHash || fileHash || null,
      cid: certificate?.cid || cid || null,
      ipfsUrl: certificate?.ipfs_url || certificate?.ipfsUrl || (certificate?.cid ? `https://gateway.pinata.cloud/ipfs/${certificate.cid}` : null),
      anchorUrl: certificate?.anchor_url || certificate?.anchorUrl || null,
      anchorTx: certificate?.anchor_tx || certificate?.anchorTx || null,
      uploadDate: certificate?.upload_date || certificate?.uploadDate || ocrData?.issueDate || null,
      authenticationResult: certificate?.authentication_result || null,
      // Include OCR extracted data
      ocrData: ocrData,
    };
  } catch (error) {
    console.error('Verification error:', error);
    throw new Error('Failed to verify certificate: ' + error.message);
  }
}

/**
 * Calculate SHA256 hash of a file using Web Crypto API
 * @param {File} file 
 * @returns {Promise<string>}
 */
async function calculateFileHash(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const arrayBuffer = e.target.result;
        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        resolve(hashHex);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Search certificates with filters and pagination
 * @param {Object} params - { institution_id?, search?, page?, page_size? }
 * @returns {Promise<Object>} { items: [], total: number }
 */
export async function searchCertificates(params) {
  try {
    const {
      institution_id = '',
      search = '',
      page = 1,
      page_size = 10,
    } = params;

    // Try to get real data from Supabase first
    try {
      const query = supabase
        .from('certificates')
        .select('*', { count: 'exact' });

      // Filter by institution
      if (institution_id && institution_id.trim()) {
        query.or(`institution_id.eq.${institution_id},institution.ilike.%${institution_id}%`);
      }

      // Search by student name, roll number, or certificate type
      if (search && search.trim()) {
        const searchTerm = `%${search.trim()}%`;
        query.or(`student_name.ilike.${searchTerm},roll_number.ilike.${searchTerm},certificate_type.ilike.${searchTerm}`);
      }

      // Order and paginate
      query.order('upload_date', { ascending: false });
      const offset = (page - 1) * page_size;
      query.range(offset, offset + page_size - 1);

      const { data, error, count } = await query;

      if (!error && data && data.length > 0) {
        // Real data found!
        return {
          items: data,
          total: count || 0,
          page: parseInt(page),
          page_size: parseInt(page_size),
          total_pages: Math.ceil((count || 0) / page_size),
        };
      }
    } catch (dbError) {
      console.warn('Supabase query failed, falling back to mock data:', dbError);
    }

    // Fallback to mock data with Indian names
    const mockCertificates = [
      {
        id: '1',
        student_name: 'Arush Sharma',
        roll_number: 'CS2021001',
        institution: 'IIT Delhi',
        certificate_type: 'Bachelor of Technology',
        upload_date: '2024-11-15T10:30:00Z',
        file_hash: 'a1b2c3d4e5f6789012345678901234567890123456789012345678901234',
        cid: 'QmX1Y2Z3A4B5C6D7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3',
        ipfs_url: 'https://gateway.pinata.cloud/ipfs/QmX1Y2Z3A4B5C6D7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3',
        anchor_url: 'https://solscan.io/tx/abc123',
        anchor_tx: 'abc123',
        authentication_result: { isAuthentic: true, confidence: 0.95 }
      },
      {
        id: '2',
        student_name: 'Bhumika Patel',
        roll_number: 'EC2021045',
        institution: 'IIT Bombay',
        certificate_type: 'Bachelor of Engineering',
        upload_date: '2024-11-10T14:20:00Z',
        file_hash: 'b2c3d4e5f6789012345678901234567890123456789012345678901234a1',
        cid: 'QmY2Z3A4B5C6D7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4',
        ipfs_url: 'https://gateway.pinata.cloud/ipfs/QmY2Z3A4B5C6D7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4',
        anchor_url: 'https://solscan.io/tx/def456',
        anchor_tx: 'def456',
        authentication_result: { isAuthentic: true, confidence: 0.92 }
      },
      {
        id: '3',
        student_name: 'Rohan Verma',
        roll_number: 'ME2020032',
        institution: 'NIT Trichy',
        certificate_type: 'Master of Technology',
        upload_date: '2024-10-25T09:15:00Z',
        file_hash: 'c3d4e5f6789012345678901234567890123456789012345678901234a1b2',
        cid: 'QmZ3A4B5C6D7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4V5',
        ipfs_url: 'https://gateway.pinata.cloud/ipfs/QmZ3A4B5C6D7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4V5',
        anchor_url: 'https://solscan.io/tx/ghi789',
        anchor_tx: 'ghi789',
        authentication_result: { isAuthentic: true, confidence: 0.88 }
      },
      {
        id: '4',
        student_name: 'Ananya Reddy',
        roll_number: 'CS2022015',
        institution: 'BITS Pilani',
        certificate_type: 'Bachelor of Science',
        upload_date: '2024-10-20T16:45:00Z',
        file_hash: 'd4e5f6789012345678901234567890123456789012345678901234a1b2c3',
        cid: 'QmA4B5C6D7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4V5W6',
        ipfs_url: 'https://gateway.pinata.cloud/ipfs/QmA4B5C6D7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4V5W6',
        anchor_url: 'https://solscan.io/tx/jkl012',
        anchor_tx: 'jkl012',
        authentication_result: { isAuthentic: true, confidence: 0.97 }
      },
      {
        id: '5',
        student_name: 'Kabir Singh',
        roll_number: 'EE2021078',
        institution: 'IIT Madras',
        certificate_type: 'Bachelor of Technology',
        upload_date: '2024-09-30T11:00:00Z',
        file_hash: 'e5f6789012345678901234567890123456789012345678901234a1b2c3d4',
        cid: 'QmB5C6D7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4V5W6X7',
        ipfs_url: 'https://gateway.pinata.cloud/ipfs/QmB5C6D7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4V5W6X7',
        anchor_url: 'https://solscan.io/tx/mno345',
        anchor_tx: 'mno345',
        authentication_result: { isAuthentic: true, confidence: 0.90 }
      },
      {
        id: '6',
        student_name: 'Diya Gupta',
        roll_number: 'CS2020098',
        institution: 'IIT Delhi',
        certificate_type: 'Master of Computer Applications',
        upload_date: '2024-09-15T13:30:00Z',
        file_hash: 'f6789012345678901234567890123456789012345678901234a1b2c3d4e5',
        cid: 'QmC6D7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4V5W6X7Y8',
        ipfs_url: 'https://gateway.pinata.cloud/ipfs/QmC6D7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4V5W6X7Y8',
        anchor_url: 'https://solscan.io/tx/pqr678',
        anchor_tx: 'pqr678',
        authentication_result: { isAuthentic: true, confidence: 0.93 }
      },
      {
        id: '7',
        student_name: 'Vivaan Kumar',
        roll_number: 'ME2021056',
        institution: 'Anna University',
        certificate_type: 'Bachelor of Engineering',
        upload_date: '2024-08-28T08:20:00Z',
        file_hash: 'g789012345678901234567890123456789012345678901234a1b2c3d4e5f6',
        cid: 'QmD7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4V5W6X7Y8Z9',
        ipfs_url: 'https://gateway.pinata.cloud/ipfs/QmD7E8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4V5W6X7Y8Z9',
        anchor_url: 'https://solscan.io/tx/stu901',
        anchor_tx: 'stu901',
        authentication_result: { isAuthentic: true, confidence: 0.89 }
      },
      {
        id: '8',
        student_name: 'Aarohi Desai',
        roll_number: 'EC2022034',
        institution: 'VIT Vellore',
        certificate_type: 'Bachelor of Technology',
        upload_date: '2024-08-10T15:10:00Z',
        file_hash: 'h89012345678901234567890123456789012345678901234a1b2c3d4e5f67',
        cid: 'QmE8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4V5W6X7Y8Z9A0',
        ipfs_url: 'https://gateway.pinata.cloud/ipfs/QmE8F9G0H1I2J3K4L5M6N7O8P9Q0R1S2T3U4V5W6X7Y8Z9A0',
        anchor_url: 'https://solscan.io/tx/vwx234',
        anchor_tx: 'vwx234',
        authentication_result: { isAuthentic: true, confidence: 0.94 }
      }
    ];

    // Filter by search term
    let filteredData = mockCertificates;
    if (search && search.trim()) {
      const searchLower = search.toLowerCase();
      filteredData = mockCertificates.filter(cert => 
        cert.student_name.toLowerCase().includes(searchLower) ||
        cert.roll_number.toLowerCase().includes(searchLower)
      );
    }

    // Filter by institution
    if (institution_id && institution_id.trim()) {
      filteredData = filteredData.filter(cert => 
        cert.institution.toLowerCase().includes(institution_id.toLowerCase())
      );
    }

    // Pagination
    const total = filteredData.length;
    const offset = (page - 1) * page_size;
    const paginatedData = filteredData.slice(offset, offset + page_size);

    return {
      items: paginatedData,
      total: total,
      page: parseInt(page),
      page_size: parseInt(page_size),
      total_pages: Math.ceil(total / page_size),
    };
  } catch (error) {
    console.error('Search error:', error);
    throw new Error('Failed to search certificates: ' + error.message);
  }
}

/**
 * Get certificate by ID
 * @param {string} id 
 * @returns {Promise<Object>}
 */
export async function getCertificateById(id) {
  try {
    const { data, error } = await supabase
      .from('certificates')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Get certificate error:', error);
    throw new Error('Failed to get certificate: ' + error.message);
  }
}

/**
 * Get certificates by student
 * @param {string} rollNumber 
 * @param {string} institution 
 * @returns {Promise<Array>}
 */
export async function getCertificatesByStudent(rollNumber, institution) {
  try {
    let query = supabase
      .from('certificates')
      .select('*')
      .eq('roll_number', rollNumber);

    if (institution) {
      query = query.eq('institution', institution);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Get certificates by student error:', error);
    throw new Error('Failed to get student certificates: ' + error.message);
  }
}

/**
 * Get statistics for HR dashboard
 * @returns {Promise<Object>}
 */
export async function getHRStatistics() {
  try {
    // Return mock statistics
    return {
      total: 8,
      valid: 8,
      recent: 5,
      suspicious: 0,
    };
  } catch (error) {
    console.error('Get statistics error:', error);
    return { total: 0, valid: 0, recent: 0, suspicious: 0 };
  }
}
